import React, { useState } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import colors from '../../../../constants/colors';
import { desktopMinWidth } from '../../../../../utils/windowHelpers';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import DangerIcon from '../../../../components/icons/DangerIcon';
import LoadingSubmissionScreen from '../../../landingPages/AnalyticLandingPage/components/LoaddingSubmissionScreen';
import { addFeedback } from 'app/containers/AnalyticsPage/actions';

const validationSchema = Yup.object().shape({
  description: Yup.string().required('This field is required'),
});

interface Values {
  description: string;
}

const FeedbackForm = () => {
  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      root: {
        '& .MuiFormControl-root': {
          marginTop: 0,
        },
        '& .MuiInputBase-input': {
          background: 'transparent',
          color: colors.light_white_e2,
        },
        '& .MuiInputBase-root': {
          background: 'transparent',
          borderColor: colors.gray_66,
        },
        padding: 16,
        borderRadius: 4,
        border: '1px solid #aaaaaa',
        background: 'transparent',
        margin: '32px auto auto auto',
        width: 320,
        [desktopMinWidth]: {
          width: 400,
        },
      },
      label: {
        marginBottom: 16,
        color: colors.white_f2,
        fontSize: 14,
        maxWidth: 320,
        [desktopMinWidth]: {
          fontSize: 16,
          maxWidth: 'initial',
        },
      },
      form: {
        display: 'flex',
      },
      formControl: {
        marginRight: 12,
        marginBottom: 0,
        marginTop: 0,
        flex: 1,
        [desktopMinWidth]: {
          marginRight: 16,
        },
      },
      formLabel: {
        fontSize: 16,
        color: colors.white_f2,
        marginBottom: 8,
      },
      buttonWrapper: {
        display: 'flex',
        justifyContent: 'center',
      },
      button: {
        height: 48,
        background: colors.brand_yellow,
        padding: '12 0',
        textAlign: 'center',
        [desktopMinWidth]: {
          width: 100,
          marginTop: 0,
        },
        '&:hover': {
          background: colors.brand_yellow,
          opacity: 0.7,
        },
      },
      errorLabel: {
        color: '#DB3952',
        background: '#FFE6DF',
        lineHeight: 1.5,
        padding: '14px 16px',
        borderRadius: 4,
        margin: '16px auto auto auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        maxWidth: '289px',
        fontSize: 14,
        [desktopMinWidth]: {
          maxWidth: '320px',
        },
      },
      successLabel: {
        marginTop: theme.spacing(2),
        color: theme.palette.success.light,
        lineHeight: '20px',
        padding: '14px 16px',
        borderRadius: 8,
        fontSize: 14,
        [desktopMinWidth]: {
          fontSize: 16,
        },
      },
      dangerIcon: {
        marginRight: 8,
        marginTop: '-2px',
      },
    }),
  );

  const classes = useStyles();

  const [errorMsg, setErrorMsg] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const submitHandler = async (
    values: Values,
    { setSubmitting }: FormikHelpers<Values>,
  ) => {
    setFormSubmitting(true);
    const { description } = values;

    try {
      const postData = {
        description,
      };
      await addFeedback(postData);
      setSuccess(true);
    } catch (error) {
      setErrorMsg(error.response?.data);
    }
    setFormSubmitting(false);
  };
  return (
    <div className={classes.root}>
      <div className={classes.label}>
        Have a collection you think Nas IO should track?
      </div>
      {!success && (
        <Formik
          initialValues={
            {
              description: null,
            } as any
          }
          // @ts-ignore
          onSubmit={submitHandler}
          validationSchema={validationSchema}
        >
          {({
            values: { description },
            errors,
            touched,
            handleChange,
            setFieldTouched,
          }) => {
            const change = (
              fieldName: string,
              e: React.ChangeEvent<HTMLInputElement>,
            ) => {
              e.persist();
              handleChange(e);
              setFieldTouched(fieldName, true, false);
            };
            return (
              <Form className={classes.form}>
                <>
                  <FormControl className={classes.formControl}>
                    <TextField
                      id="description"
                      name="description"
                      helperText={touched.description ? errors.description : ''}
                      error={touched.description && Boolean(errors.description)}
                      value={description}
                      onChange={change.bind(null, 'description')}
                      type="text"
                      variant="outlined"
                      className={classes.formControl}
                      placeholder="Input collection description"
                      required={true}
                    />
                  </FormControl>
                </>
                <div className={classes.buttonWrapper}>
                  <Button className={classes.button} type="submit">
                    SUBMIT
                  </Button>
                </div>
                {errorMsg && (
                  <div className={classes.errorLabel}>
                    <span className={classes.dangerIcon}>
                      <DangerIcon />
                    </span>
                    <span>{errorMsg}</span>
                  </div>
                )}
              </Form>
            );
          }}
        </Formik>
      )}
      {success && (
        <div className={classes.successLabel}>
          Your suggestion is submitted!
        </div>
      )}
      {formSubmitting && <LoadingSubmissionScreen />}
    </div>
  );
};

export default FeedbackForm;
